// Types and value rules of the UI -> New Relic event contract
// (tetris-java/docs/newrelic/ui-event-contract.md, v1.0).

export const CONTRACT = { name: 'UI_EVENTS', version: '1.0' } as const;

export const ATTR = {
  component: 'component', page: 'page', mode: 'mode', viewId: 'viewId', uiInstanceId: 'uiInstanceId',
  jobId: 'jobId', aisleCode: 'aisleCode', action: 'action', visible: 'visible', focused: 'focused',
  success: 'success', seq: 'seq', clientTimestamp: 'clientTimestamp', elapsedMs: 'elapsedMs',
  jobIdState: 'jobIdState', openCause: 'openCause', eventId: 'eventId',
  contract: 'contract', contractVersion: 'contractVersion', downloadType: 'downloadType',
  pogKind: 'pogKind', source: 'source', fileName: 'fileName', bulkCount: 'bulkCount',
  pogLinearSize: 'pogLinearSize', targetLinearMetric: 'targetLinearMetric', linearDelta: 'linearDelta',
  linearMatch: 'linearMatch', productivityEstimate: 'productivityEstimate',
} as const;

export type AttrKey = typeof ATTR[keyof typeof ATTR];
export type JobIdState = 'RESOLVED' | 'UNRESOLVED' | 'NOT_APPLICABLE';
export type OpenCause = 'START' | 'SWITCH' | 'RESUME';

export const EVENT_TYPES = {
  Traffic: 'Traffic',
  SeedView: 'SeedView',
  SeedDownload: 'SeedDownload',
  MerchGuideDownload: 'MerchGuideDownload',
} as const;

export type EventType = typeof EVENT_TYPES[keyof typeof EVENT_TYPES];

export type TelemetryComponent =
  'WELCOME' | 'DASHBOARD' | 'SEED' | 'REVIEW' | 'EDITOR' | 'PEGX' | 'ADMIN' | 'OTHER';

export type TelemetryMode = 'VIEW' | 'PREVIEW' | 'EDIT' | 'COMPARE';

export type TrafficAction = 'probe' | 'page_open' | 'focus' | 'blur' | 'view_close';

export type DownloadType =
  | 'SAVE_TO_STORE'
  | 'PUSH_POG_TO_STORE'
  | 'PRINT_PDF'
  | 'CONNECT_PLUS_PDF'
  | 'EXPORT'
  | 'PEGX_PDF'
  | 'PROTO_EXPORT'
  | 'DATA_FILE';

/** Entry points of the Execution Guide (contract §4.4). */
export type MerchGuideSource = 'SEED_PAGE' | 'PROTO_ROW' | 'PROTO_BULK';

/** NR attribute values are capped at 4096 characters. */
export const MAX_ATTRIBUTE_LENGTH = 4096;

/**
 * `fileName` of MerchGuideDownload: the guide link without query string and fragment, which may carry
 * access tokens (contract §4.4).
 */
export const toGuideFileName = (link?: string | null): string =>
  (link || '').split(/[?#]/)[0].slice(0, MAX_ATTRIBUTE_LENGTH);

export type PogKind = 'SHARED_POG' | 'PRIVATE_EDITED' | 'EDITED_PROTO' | 'PDF_PROTO' | 'OPTIMIZED';

export type LinearMatch = 'MATCH' | 'MISMATCH' | 'UNSET';

export type AttributeValue = string | number;

export type Attributes = Record<string, AttributeValue>;

/** Injected by the backend; anything the UI sends under these keys is discarded (contract §2). */
export const RESERVED_ATTRIBUTES: ReadonlyArray<string> = [
  'username',
  'userDomain',
  'role',
  'sessionId',
  'activeSessionId',
  'correlationId',
  'environment',
  'application',
  'datetime',
  'timestamp',
  'schemaVersion',
  'eventType',
];

export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const toFlag = (value: boolean): string => (value ? 'true' : 'false');

/**
 * Normalises values to the contract rules (§3.1): numbers stay numbers, booleans become
 * "true"/"false", null becomes "". Undefined keys are dropped so they do not override defaults.
 */
export const sanitizeAttributes = (input: Record<string, unknown>): Attributes => {
  const result: Attributes = {};

  Object.keys(input).forEach((key) => {
    const value = input[key];

    if (value === undefined || RESERVED_ATTRIBUTES.includes(key)) {
      return;
    }

    if (value === null) {
      result[key] = '';
    } else if (typeof value === 'number') {
      result[key] = Number.isFinite(value) ? value : '';
    } else if (typeof value === 'boolean') {
      result[key] = toFlag(value);
    } else if (typeof value === 'string') {
      result[key] = value;
    }
  });

  return result;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

export interface LinearInput {
  pogLinearSize?: number | null;
  targetLinearMetric?: number | null;
}

/**
 * Linear attributes of SeedDownload (§4.3). `UNSET` whenever there is no target linear to compare
 * against: sending MISMATCH without a target would make the linear match rate meaningless.
 */
export const buildLinearAttributes = ({ pogLinearSize, targetLinearMetric }: LinearInput): Attributes => {
  const pog = isFiniteNumber(pogLinearSize) ? round2(pogLinearSize) : undefined;
  const target = isFiniteNumber(targetLinearMetric) ? round2(targetLinearMetric) : undefined;
  const result: Attributes = { [ATTR.linearMatch]: 'UNSET' };

  if (pog !== undefined) {
    result[ATTR.pogLinearSize] = pog;
  }

  if (target !== undefined) {
    result[ATTR.targetLinearMetric] = target;
  }

  if (pog !== undefined && target !== undefined) {
    const delta = round2(pog - target);

    result[ATTR.linearDelta] = delta;
    result[ATTR.linearMatch] = delta === 0 ? 'MATCH' : 'MISMATCH';
  }

  return result;
};

/** Linear feet of an aisle geometry, computed exactly like the seed table does (`sum(width) / 12`). */
export const linearFeetOfBlocks = (blocks?: Array<{ width: number }> | null): number | undefined => {
  if (!blocks?.length) {
    return undefined;
  }

  const inches = blocks.reduce((sum, block) => sum + (isFiniteNumber(block.width) ? block.width : 0), 0);

  return inches > 0 ? inches / 12 : undefined;
};
